import React, { useState, useEffect } from 'react';
import { Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Alert } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@apollo/client';
import yup from 'validation';
import i18next from 'i18next';
import moment from 'moment';

import { FormInputDropdown } from 'components/form/FormInputDropdown';
import { FormInputPercentage } from 'components/form/FormInputPercentage';
import { FormInputDate } from 'components/form/FormInputDate';
import { FormInputTextArea } from 'components/form/FormInputText';
import SimpleTable from 'components/table/SimpleTable';
import { formatDate } from 'components/DateTime';
import { formatPercentage } from 'components/Percentage';
import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { useDispatch } from 'react-redux';

import {
  HOTEL_COMMISSION_RATES_QUERY,
  UPSERT_HOTEL_COMMISSION_RATE_MUTATION,
  DELETE_HOTEL_COMMISSION_RATE_MUTATION,
} from '../gql';
import { EOfferCommissionBase, ECustomerType, ListHotelCommissionRatesQuery } from '__generated__/graphql';

const commissionRateSchema = yup.object().shape({
  customerType: yup.mixed<ECustomerType>().oneOf(Object.values(ECustomerType)).required(),
  commissionBase: yup.mixed<EOfferCommissionBase>().oneOf(Object.values(EOfferCommissionBase)).required(),
  commissionPercentage: yup.number().required().min(0).max(100),
  validFrom: yup.date().required().min(moment().startOf('day').toDate(), i18next.t('hotel-commission-validation-past-date') as string),
  remark: yup.string().nullable(),
});
interface HotelCommissionTabProps {
  hotelId: number;
}

/**
 * Manages commission rate configurations for a hotel.
 * Rates are frozen when offers are created to ensure consistency.
 */
export default function HotelCommissionTab({ hotelId }: HotelCommissionTabProps) {
  const dispatch = useDispatch();
  
  const { data, loading, refetch } = useQuery(HOTEL_COMMISSION_RATES_QUERY, {
    variables: { hotelId },
    skip: hotelId === 0,
  });

  const [upsertRate, { loading: upserting }] = useMutation(UPSERT_HOTEL_COMMISSION_RATE_MUTATION);
  const [deleteRate, { loading: deleting }] = useMutation(DELETE_HOTEL_COMMISSION_RATE_MUTATION);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const form = useForm({
    mode: 'all',
    resolver: yupResolver(commissionRateSchema) as any,
    defaultValues: {
      customerType: ECustomerType.CLASSIC,
      commissionBase: EOfferCommissionBase.GROSS,
      commissionPercentage: 0,
      validFrom: moment().add(1, 'day').startOf('day').toDate(),
      remark: '',
    },
  });

  const handleSave = async (values: any) => {
    try {
      await upsertRate({
        variables: {
          hotelId,
          input: {
            customerType: values.customerType,
            commissionBase: values.commissionBase,
            commissionPercentage: values.commissionPercentage / 100,
            validFrom: values.validFrom,
            remark: values.remark || null,
          },
        },
      });
      refetch();
      setIsDialogOpen(false);
      form.reset();
      dispatchMessage(dispatch, i18next.t('hotel-commission-saved'));
    } catch (error: any) {
      if (error.message.includes('DUPLICATE_RATE')) {
        dispatchException(dispatch, new Error(i18next.t('hotel-commission-error-duplicate') as string));
      } else if (error.message.includes('VALIDATION_ERROR')) {
        dispatchException(dispatch, new Error(i18next.t('hotel-commission-validation-past-date') as string));
      } else {
        dispatchException(dispatch, error);
      }
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteRate({ variables: { id } });
      refetch();
      dispatchMessage(dispatch, i18next.t('hotel-commission-deleted'));
    } catch (error) {
      dispatchException(dispatch, error);
    }
  };

  const handleAdd = () => {
    form.reset({
      customerType: ECustomerType.CLASSIC,
      commissionBase: EOfferCommissionBase.GROSS,
      commissionPercentage: 0,
      validFrom: moment().add(1, 'day').startOf('day').toDate(),
      remark: '',
    });
    setIsDialogOpen(true);
  };

  if (loading) return <div>{i18next.t('loading')}</div>;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert 
          severity="info"
          sx={{ 
            backgroundColor: '#f0e1e9',
            color: '#ac2a6e',
            border: '1px solid #ac2a6e',
            '& .MuiAlert-icon': {
              color: '#ac2a6e'
            }
          }}
        >
          <strong>{i18next.t('hotel-commission-note-label')}:</strong> {i18next.t('hotel-commission-rates-info')}
        </Alert>
      </Grid>
      
      
      <Grid item xs={12}>
        <SimpleTable
          headers={[
            i18next.t('hotel-commission-customer-type'),
            i18next.t('hotel-commission-base'),
            i18next.t('hotel-commission-percentage'),
            i18next.t('hotel-commission-valid-from'),
            i18next.t('hotel-commission-remark'),
            '',
          ]}
          rows={(data?.listHotelCommissionRates || []).map((rate: ListHotelCommissionRatesQuery['listHotelCommissionRates'][number]) => [
            i18next.t(`filter-customer-type-${rate.customerType.toLowerCase()}`),
            i18next.t(`enums-EOfferCommissionBase-${rate.commissionBase}`),
            formatPercentage(rate.commissionPercentage),
            formatDate(new Date(rate.validFrom)),
            rate.remark || '-',
            <IconButton
              size="small"
              onClick={() => handleDelete(rate.id)}
              disabled={deleting}
            >
              <DeleteIcon />
            </IconButton>,
          ])}
        />
      </Grid>
      
      <Grid item xs={12}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={hotelId === 0}
        >
          {i18next.t('hotel-commission-add')}
        </Button>
      </Grid>

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{i18next.t('hotel-commission-dialog-title')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormInputDropdown
                name="customerType"
                control={form.control}
                label={i18next.t('hotel-commission-customer-type')}
                options={[
                  { value: ECustomerType.CLASSIC, label: i18next.t('filter-customer-type-classic') },
                  { value: ECustomerType.PREMIUM, label: i18next.t('filter-customer-type-premium') },
                ]}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormInputDropdown
                name="commissionBase"
                control={form.control}
                label={i18next.t('invoice-commission-base')}
                options={Object.values(EOfferCommissionBase).map((cb) => ({
                  label: i18next.t(`enums-EOfferCommissionBase-${cb}`),
                  value: cb,
                }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormInputPercentage
                name="commissionPercentage"
                control={form.control}
                label={i18next.t('invoice-commission-percentage')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormInputDate
                name="validFrom"
                control={form.control}
                label={i18next.t('hotel-commission-valid-from')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormInputTextArea
                name="remark"
                control={form.control}
                label={i18next.t('hotel-commission-remark')}
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>{i18next.t('cancel')}</Button>
          <Button
            variant="contained"
            onClick={form.handleSubmit(handleSave)}
            disabled={upserting}
          >
            {i18next.t('hotel-commission-save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}